// BRK-S183 — where a filed bond request is in its life after submission
// (SIG-S021 decisions 5, 7 and 8; the vocabulary is PMS-S487's).
//
// `status` is the SURETY service's mirrored draft / submitted / cancelled and
// stops at "submitted". Everything after that — the P&P request, a capacity
// wait, an underwriting review, issuance, the invoice, payment — is PMS's own
// `workflowStatus`, published TOP-LEVEL beside `status` on the broker order view
// and on each list row, optional and nullable. It is a second axis, never new
// values on `status`, which is why the list filters it with its own
// `?workflowStatus=` rather than widening `?status=`.
//
// ── WHY THE MIRROR TOLERATES WHAT IT DOES NOT KNOW ───────────────────────────
//
// Adopted consumer-first (BRK-S183) and pinned at BRK-S280, where
// `test/lib/workflowStatusContract.test.ts` holds the vocabulary below equal to
// the one PMS publishes on the order view, the list row and the filter. It is
// a CLOSED mirror so each value gets deliberate words, but the reader must not
// break on a value it has not adopted yet: a new state added to PMS's transition
// table would otherwise blank a chip or throw on a list row. So:
//   • `null` / absent — no post-submission step yet. Renders NOTHING.
//   • a mirrored value — its label.
//   • anything else — the neutral "In progress". Never a blank, never the raw
//     wire value (which could carry vocabulary a broker must not read).
//
// ── DISCRETION (decision 8) ──────────────────────────────────────────────────
//
// `underwriter_review` is LABELLED "In review". The wire value is unchanged,
// but no broker screen carries underwriting vocabulary, and no score, tier or
// reason rides along with the status: PMS publishes the transition's reason on
// the admin read only. `test/lib/workflowStatus.test.ts` sweeps every label.

/** PMS-S487's vocabulary, in lifecycle order. */
export const WORKFLOW_STATUSES = [
  "pp_requested",
  "awaiting_capacity",
  "underwriter_review",
  "issued",
  "invoiced",
  "paid",
  "complete",
  "closed",
] as const;

export type BondWorkflowStatus = (typeof WORKFLOW_STATUSES)[number];

/** Is this raw value one this build has mirrored? */
export function isWorkflowStatus(value: unknown): value is BondWorkflowStatus {
  return (
    typeof value === "string" &&
    (WORKFLOW_STATUSES as readonly string[]).includes(value)
  );
}

const LABELS: Record<BondWorkflowStatus, string> = {
  pp_requested: "P&P requested",
  awaiting_capacity: "Awaiting capacity",
  // Decision 8: the broker's word for it. The wire value stays as it is.
  underwriter_review: "In review",
  issued: "Issued",
  invoiced: "Invoiced",
  paid: "Paid",
  complete: "Complete",
  closed: "Closed",
};

/** What a value this build has not mirrored reads as. */
export const UNKNOWN_WORKFLOW_LABEL = "In progress";

/**
 * The label for a raw `workflowStatus`, or **null** for "render nothing".
 *
 * Takes `unknown` on purpose: the key is optional, nullable and — once PMS
 * widens its transition table — may carry a value this bundle predates.
 */
export function workflowStatusLabel(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return isWorkflowStatus(value) ? LABELS[value] : UNKNOWN_WORKFLOW_LABEL;
}

/** The chip palette, beside the status chip. Neutral for an unknown value. */
export function workflowStatusBadgeClass(value: unknown): string {
  if (!isWorkflowStatus(value)) return "bg-brk-bg text-brk-dim";
  switch (value) {
    case "issued":
    case "paid":
    case "complete":
      return "bg-green-100 dark:bg-green-400/15 text-green-700 dark:text-green-300";
    case "awaiting_capacity":
    case "underwriter_review":
      return "bg-amber-100 dark:bg-amber-400/15 text-amber-700 dark:text-amber-300";
    case "closed":
      return "bg-brk-bg text-brk-dim";
    default:
      return "bg-blue-100 dark:bg-blue-400/15 text-blue-700 dark:text-blue-300";
  }
}

/** The list's `?workflowStatus=` options, in lifecycle order. */
export const WORKFLOW_STATUS_OPTIONS: {
  value: BondWorkflowStatus;
  label: string;
}[] = WORKFLOW_STATUSES.map((value) => ({ value, label: LABELS[value] }));
